<template>
    <div>
        <v-card flat>
            <v-card-title class="mb-0 pb-0">
                <v-row>
                    <v-col>
                        {{ $t("sales_dashboard") }} 
                    </v-col>
                    <v-col>

                    </v-col>
                    <v-col>
                        <v-menu
                            ref="date_from"
                            v-model="menu.date_from"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    dense
                                    v-model="form.date_from"
                                    :rules="rules.date_from"
                                    :label="$t('date_from')"
                                    append-icon="mdi-calendar"
                                    hide-details
                                    readonly
                                    v-bind="attrs"
                                    v-on="on">
                                </v-text-field>
                            </template>
                            <v-date-picker
                                :max="form.date_to"
                                v-model="form.date_from" 
                                @input="menu.date_from = false" 
                                no-title 
                                scrollable
                                @change="fetchDashboardData">
                            </v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col>
                        <v-menu
                            ref="date_to"
                            v-model="menu.date_to"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="290px">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                    dense
                                    v-model="form.date_to"
                                    :rules="rules.date_to"
                                    :label="$t('date_to')"
                                    append-icon="mdi-calendar"
                                    hide-details
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                    >
                                </v-text-field>
                            </template>
                            <v-date-picker
                                v-model="form.date_to" 
                                @input="menu.date_to = false" 
                                no-title 
                                scrollable
                                :min="form.date_from"
                                @change="fetchDashboardData">
                            </v-date-picker>
                        </v-menu>

                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-subtitle class="mb-0 pb-0">
                <v-row>
                    <v-col>
                        {{ lastUpdateFormatted ? "Updated" : "" }} {{ lastUpdateFormatted }}
                    </v-col>
                </v-row>
            </v-card-subtitle>
            <v-card-text v-if="loading.data">
                <v-row class="mt-4">
                    <v-col>
                        <v-skeleton-loader type="image"></v-skeleton-loader>
                    </v-col>
                    <v-col>
                        <v-skeleton-loader type="image"></v-skeleton-loader>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-skeleton-loader type="image"></v-skeleton-loader>
                    </v-col>
                    <v-col>
                        <v-skeleton-loader type="image"></v-skeleton-loader>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-skeleton-loader type="image"></v-skeleton-loader>
                    </v-col>
                    <v-col>
                        <v-skeleton-loader type="image"></v-skeleton-loader>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-text v-else>
                <v-row>
                    <v-col cols="6">
                        <v-card flat>
                            <v-subheader>Sales Agreement by Product</v-subheader>
                            <v-card-text>
                                <canvas id="salesOrdersByProduct" height="50" width="100"></canvas>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="6">
                        <v-card flat>
                            <v-subheader>Price Range</v-subheader>
                            <v-simple-table dense>
                                <thead>
                                    <tr>
                                        <th>{{ $t("product") }}</th>
                                        <th>{{ $t("quantity") }}</th>
                                        <!-- <th>{{ $t("uom") }}</th> -->
                                        <th>{{ $t("min") }}</th>
                                        <th>{{ $t("max") }}</th>
                                        <th>{{ $t("avg") }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="order in salesOrdersByProduct" :key="order.item_id">
                                        <td><b>{{ order.item }}</b></td>
                                        <td>{{ formatNumber(order.quantity) }}</td>
                                        <!-- <td>{{ order.measure_unit }}</td> -->
                                        <td>{{ formatNumber(order.price_min) }}</td>
                                        <td>{{ formatNumber(order.price_max) }}</td>
                                        <td><b>{{ formatNumber(order.price_avg) }}</b></td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6">
                        <v-card flat>
                            <v-subheader>Price Index</v-subheader>
                            <v-card-text>
                                <v-select dense hide-details
                                    v-model="priceItem"
                                    :items="items"
                                    @change="priceChangeChart(priceItem)">
                                </v-select>
                                <canvas ref="changesInPrice" height="50" width="100"></canvas>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="6">
                        <v-card flat>
                            <v-subheader>Demand Trend</v-subheader>
                            <v-card-text>
                                <v-select dense hide-details
                                    v-model="quantityItem"
                                    :items="items"
                                    @change="quantityChangeChart(quantityItem)">
                                </v-select>
                                <canvas id="quantityChangeGraph" height="50" width="100"></canvas>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import Chart from 'chart.js';
import { mapActions } from 'vuex';
import { formatNumber } from 'accounting';
import { map, uniq, filter, random } from 'lodash';
import moment from 'moment';

export default {
    components: {
        
    },
    data () {
        return {
            loading: {
                data: false
            },
            priceItem: "C25 350kg",
            quantityItem: "C25 350kg",
            salesOrdersByProduct: [],
            chartData: {
                priceChange: []
            },
            lastUpdate: Date.now(),
            lastUpdateFormatted: null,
            form: {
                date_from: null,
                date_to: null
            },
            rules: {
                date_from: [

                ],
                date_to: [

                ]
            },
            menu: {
                date_from: null,
                date_to: null
            },
            priceChangeChartObject: null,
            demandTrendChartObject: null
        }
    },
    computed: {
        items() {
            var items = map(this.chartData.priceChange, "item");
            items = uniq(items);
            return items;
        }
    },
    async mounted () {
        this.form.date_from = moment().subtract(1, 'days').format("YYYY-MM-DD");
        this.form.date_to = moment().format("YYYY-MM-DD");
        this.lastUpdate = Date.now();
        this.fetchDashboardData();
        setInterval(() => {
            this.fetchDashboardData();
        }, 1000 * 60 * 10);

        setInterval(() => {
            this.lastUpdateFormatted = moment(this.lastUpdate).startOf('seconds').fromNow();
        }, 1000 * 10);
    },
    methods: {
        formatNumber,
        ...mapActions("sales/dashboard", ["fetchSalesOrdersByProduct", "fetchPriceChangeChartData"]),
        salesOrderQuantityByProductChart() {
            const ctx = document.getElementById("salesOrdersByProduct");
            new Chart(ctx, {
                type: "bar",
                data: {
                    labels: map(this.salesOrdersByProduct, "item"),
                    datasets: [
                        {
                            label: "Product",
                            data: map(this.salesOrdersByProduct, "quantity"),
                            borderWidth: 1,
                            backgroundColor: [" #aed6f1 ", " #2e86c1 ", " #5dade2 ", " #2e86c1 ", " #2e86c1 ", " #2e86c1 "],
                            borderColor: ["white"]
                        }
                    ]
                },
                options: {
                    responsive: true,
                    lineTension: 1,
                    scales: {
                    yAxes: [{
                        ticks: {
                            beginAtZero: true,
                            padding: 25,
                        }
                    }]
                    }
                }
            });
        },
        priceChangeChart(item) {
            var datasets = [];
            var labels = [];

            var data = filter(this.chartData.priceChange, (each) => each.item == item)
            // var data = this.chartData.priceChange;
            datasets.push(
                {
                    label: item,
                    data: map(data, "price"),
                    borderWidth: 1,
                    borderColor: "black",
                    backgroundColor: ["#1b2631", "#1c2833", "#273746", "#566573", "#abb2b9"][random(4)]
                }
            )
            labels = (map(data, "date"))

            // const ctx = document.getElementById("changesInPrice");
            if (this.priceChangeChartObject) {
                this.priceChangeChartObject.destroy();
            }
            const ctx = this.$refs.changesInPrice;

            this.priceChangeChartObject = new Chart(ctx, {
                type: "line",
                data: {
                    labels,
                    datasets
                },
                options: {
                    responsive: true,
                    lineTension: 1,
                    scales: {
                    yAxes: [{
                        ticks: {
                            beginAtZero: true,
                            padding: 25,
                        }
                    }]
                    }
                }
            });
        },
        quantityChangeChart(item) {
            var datasets = [];
            var labels = [];

            var data = filter(this.chartData.priceChange, (each) => each.item == item)
            datasets.push(
                {
                    label: item,
                    data: map(data, "quantity"),
                    borderWidth: 1,
                    borderColor: "black",
                    backgroundColor: ["#1b2631", "#1c2833", "#273746", "#566573", "#abb2b9"][random(4)]
                }
            )
            labels = (map(data, "date"))

            if(this.demandTrendChartObject) {
                this.demandTrendChartObject.destroy()
            }

            const ctx = document.getElementById("quantityChangeGraph");
            this.demandTrendChartObject = new Chart(ctx, {
                type: "line",
                data: {
                    labels,
                    datasets
                },
                options: {
                    responsive: true,
                    lineTension: 1,
                    scales: {
                    yAxes: [{
                        ticks: {
                            beginAtZero: true,
                            padding: 25,
                        }
                    }]
                    }
                }
            });
        },
        fetchDashboardData() {
            this.loading.data = true;
            Promise.all([
                this.fetchSalesOrdersByProduct(this.form),
                this.fetchPriceChangeChartData(this.form)
            ]).then((result) => {
                this.lastUpdate = Date.now();
                this.loading.data = false;
                this.salesOrdersByProduct = result[0].data;
                this.chartData.priceChange = result[1].data;
            }).then(() => {
                this.salesOrderQuantityByProductChart();
                this.priceChangeChart(this.priceItem);
                this.quantityChangeChart(this.quantityItem);
            });
        }
    }
}
</script>

<style>
</style>
